import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { CancelSchedule, Open, ScheduleEdit } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { isoToShortDate } from "@/utils/datetime";
import {
  REPORT_ICON_COLUMN_SIZE,
  ReportIcon,
  ScheduleSummaryCell,
  TruncatedTextCell
} from "@/components/global/Chat/components/dreamerReports/reportTableCells";
import { ReportingScheduler } from "@/entities/reportingScheduler/types";
import {
  RECURRING_REPORTS_TRANSLATION_KEY,
  TRANSLATION_KEY
} from "@/components/global/Chat/components/dreamerReports/consts";

interface UseRecurringReportColumnsArgs {
  t: TFunction;
  untitledLabel: string;
  cancelPending: boolean;
  onEdit: (schedule: ReportingScheduler) => void;
  onCancel: (schedule: ReportingScheduler) => void;
  onOpenLastReport: (schedule: ReportingScheduler) => void;
}

export const useRecurringReportColumns = ({
  t,
  untitledLabel,
  cancelPending,
  onEdit,
  onCancel,
  onOpenLastReport
}: UseRecurringReportColumnsArgs): ColumnDef<ReportingScheduler, unknown>[] =>
  useMemo(
    () => [
      {
        id: "icon",
        header: "",
        enableSorting: false,
        size: REPORT_ICON_COLUMN_SIZE,
        cell: () => <ReportIcon />
      },
      {
        id: "name",
        accessorKey: "name",
        header: t(`${TRANSLATION_KEY}.nameHeader`),
        size: 250,
        cell: ({ row }) => (
          <TruncatedTextCell variant="body">
            {row.original.name || untitledLabel}
          </TruncatedTextCell>
        )
      },
      {
        id: "nextRunAt",
        accessorKey: "nextRunAt",
        header: t(`${RECURRING_REPORTS_TRANSLATION_KEY}.nextDeliveryDate`),
        cell: ({ row }) => (
          <Text as="div">
            {row.original.nextRunAt
              ? isoToShortDate(row.original.nextRunAt)
              : t(`${RECURRING_REPORTS_TRANSLATION_KEY}.noNextRun`)}
          </Text>
        )
      },
      {
        id: "schedule",
        header: t(`${RECURRING_REPORTS_TRANSLATION_KEY}.schedule`),
        enableSorting: false,
        size: 300,
        cell: ({ row }) => (
          <ScheduleSummaryCell
            t={t}
            scheduleConfig={row.original.scheduleConfig}
          />
        )
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        size: 240,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <IconButton
              size="xl"
              onClick={() => onEdit(row.original)}
              aria-label={t(
                `${RECURRING_REPORTS_TRANSLATION_KEY}.editAriaLabel`
              )}
              data-test="recurring-report-edit"
              variant="iconNoBg"
              className="shrink-0"
            >
              <ScheduleEdit />
            </IconButton>
            <IconButton
              size="xl"
              onClick={() => onCancel(row.original)}
              disabled={cancelPending}
              aria-label={t(
                `${RECURRING_REPORTS_TRANSLATION_KEY}.cancelAriaLabel`
              )}
              data-test="recurring-report-cancel"
              variant="iconNoBg"
              className="shrink-0"
            >
              <CancelSchedule />
            </IconButton>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => onOpenLastReport(row.original)}
              // Gated on the message id, not the attachment: the message id is
              // what says whether this schedule has ever delivered, and a
              // schedule that has not has nothing to open. A delivery whose
              // attachment is missing still lets the click through, so it
              // surfaces as the open failure it is rather than as a button that
              // went grey for no visible reason.
              disabled={!row.original.lastReportMessageId}
              aria-label={t(
                `${RECURRING_REPORTS_TRANSLATION_KEY}.openLastReportAriaLabel`
              )}
              data-test="recurring-report-open-last"
              className="py-[6px] px-2 flex shrink-0"
            >
              <IconContainer size="5">
                <Open />
              </IconContainer>
              {t(`${RECURRING_REPORTS_TRANSLATION_KEY}.openLastReport`)}
            </Button>
          </div>
        )
      }
    ],
    [t, untitledLabel, cancelPending, onEdit, onCancel, onOpenLastReport]
  );
